import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

import { APP_NAME } from './constants';

// Preserve historical behavior for rollout env flags: an explicitly-empty value should
// behave like 0 (disabled), not an implicit full rollout.
const emptyStringToZero = <Schema extends z.ZodTypeAny>(schema: Schema) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim().length === 0 ? 0 : value),
    schema,
  );

const commaSeparatedNumberArray = z.preprocess((value) => {
  if (value === undefined || value === null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return [];
  }

  return trimmed
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .split(',')
    .map((entry) => entry.trim())
    .map((entry) => entry.replace(/^['"]|['"]$/g, ''))
    .filter(Boolean)
    .map(Number);
}, z.array(z.number().int()));

const supabasePublishableKeySchema = z
  .string()
  .startsWith(
    'sb_publishable_',
    'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY must use a Supabase publishable key',
  );

const supabaseSecretKeySchema = z
  .string()
  .startsWith('sb_secret_', 'SUPABASE_SECRET_KEY must use a Supabase secret key');

const stripeSecretKeySchema = z
  .string()
  .min(1)
  .refine(
    (value) => value.startsWith('sk_') || value.startsWith('rk_'),
    'STRIPE_SECRET_KEY must use a Stripe secret key or restricted key',
  );

export const Env = createEnv({
  server: {
    ARCJET_KEY: z.string().startsWith('ajkey_').optional(),
    DATABASE_URL: z.string().optional(),
    DATABASE_URL_QUEUE: z.string().optional(),
    DIRECT_URL: z.string().optional(),
    LOGTAIL_SOURCE_TOKEN: z.string().optional(),
    ANTHROPIC_API_KEY: z.string().optional(),
    OPENAI_API_KEY: z.string().optional(),
    DIGITALOCEAN_INFERENCE_API_KEY: z.string().optional(),
    DIGITALOCEAN_INFERENCE_BASE_URL: z.string().url().optional(),
    OPENROUTER_API_KEY: z.string().startsWith('sk-or-').optional(),
    DOUBLEWORD_API_KEY: z.string().min(1).optional(),
    TYPESAFE_API_KEY: z.string().trim().min(1).optional(),
    DOUBLEWORD_BASE_URL: z.string().url().optional(),
    GPT_CODEX_SUBSCRIPTION_INSTALLATION_IDS: commaSeparatedNumberArray.optional(),
    AI_GATEWAY_API_KEY: z.string().optional(),
    VERCEL_AI_GATEWAY_API_KEY: z.string().optional(),
    VERCEL_OIDC_TOKEN: z.string().optional(),
    // Set by Vercel when the /code attachments Blob store is connected to the project: the
    // store's read-write token, or its ID for OIDC auth. The Blob SDK reads them itself.
    BLOB_STORE_ID: z.string().optional(),
    BLOB_READ_WRITE_TOKEN: z.string().optional(),
    DATADOG_API_KEY: z.string().optional(),
    DATADOG_APPLICATION_KEY: z.string().optional(),
    DATADOG_SITE: z.string().optional(),
    DATADOG_APPLICATION_KEY_ID: z.string().optional(),
    GITHUB_APP_ID: z.string().optional(),
    GITHUB_APP_PRIVATE_KEY_BASE64: z.string().optional(),
    GITHUB_APP_WEBHOOK_SECRET: z.string().optional(),
    GITHUB_PUBLIC_ACCESS_TOKEN: z.string().optional(),
    // A cubic Code sandbox sets it to the placeholder its firewall swaps for the session
    // repository's token (`sandbox-github-access.ts`), which `getInstallationOctokit` then sends.
    GITHUB_TOKEN: z.string().optional(),
    CUBIC_REVIEW_RUNTIME: z.enum(['production', 'eval-offline']).optional(),
    CUBIC_EVAL_CLI: z.string().optional(),
    // Set by the sandbox eval runner on each eval CLI run (never in production);
    // used to tag PostHog AI traces so eval runs are findable + isolated from
    // real reviews. In the sandbox both are in the process env at launch, so the
    // Env snapshot captures them.
    EVAL_RUN_ID: z.string().optional(),
    EVAL_DATASET_ID: z.string().optional(),
    WEBHOOK_PROXY_URL: z.string().optional(),
    NANGO_WEBHOOK_PROXY_URL: z.string().optional(),
    SUPABASE_AUTH_GITHUB_CLIENT_ID: z.string().optional(),
    SUPABASE_AUTH_GITHUB_SECRET: z.string().optional(),
    AUTO_APPROVAL_GITHUB_OAUTH_CLIENT_ID: z.string().optional(),
    AUTO_APPROVAL_GITHUB_OAUTH_CLIENT_SECRET: z.string().optional(),
    SUPABASE_SECRET_KEY: supabaseSecretKeySchema,
    SENTRY_SUPPRESS_TURBOPACK_WARNING: z.string().optional(),
    TENOR_API_KEY: z.string().optional(),
    FRAMER_API_KEY: z.string().optional(),
    FRAMER_PROJECT_URL: z.string().optional(),
    // IndexNow key (8-128 letters, digits or dashes); served at /indexnow-key.txt.
    INDEXNOW_KEY: z
      .string()
      .regex(/^[a-z0-9-]{8,128}$/i)
      .optional(),
    KV_URL: z.string().min(1),
    KV_REST_API_READ_ONLY_TOKEN: z.string().min(1),
    KV_REST_API_TOKEN: z.string().min(1),
    KV_REST_API_URL: z.string().min(1),
    NODE_ENV: z.enum(['development', 'production', 'test']),
    CRON_SECRET: z.string().min(1),
    NANGO_SECRET_KEY: z.string().min(1),
    NANGO_WEBHOOK_SIGNING_KEY: z.string().min(1).optional(),
    SLACK_CLIENT_ID: z.string().min(1),
    SLACK_CLIENT_SECRET: z.string().min(1),
    SLACK_ENCRYPTION_KEY: z.string().min(1).optional(),
    SLACK_SIGNING_SECRET: z.string().min(1).optional(),
    LINEAR_CLIENT_ID: z.string().min(1),
    LINEAR_CLIENT_SECRET: z.string().min(1),
    AZURE_RESOURCE_NAME: z.string().optional(),
    AZURE_API_KEY: z.string().optional(),
    AZURE_SECONDARY_RESOURCE_NAME: z.string().optional(),
    AZURE_SECONDARY_API_KEY: z.string().optional(),
    AZURE_TERTIARY_RESOURCE_NAME: z.string().optional(),
    AZURE_TERTIARY_API_KEY: z.string().optional(),
    AZURE_QUATERNARY_RESOURCE_NAME: z.string().optional(),
    AZURE_QUATERNARY_API_KEY: z.string().optional(),
    DEEPSEEK_API_KEY: z.string().min(1),
    GOOGLE_GENERATIVE_AI_API_KEY: z.string().optional(),
    GOOGLE_VERTEX_PROJECT: z.string().optional(),
    GOOGLE_VERTEX_SERVICE_ACCOUNT: z.string().optional(),
    GOOGLE_VERTEX_API_KEY: z.string().optional(), // Vertex AI Express mode (API key auth)
    MODAL_KEY: z.string().optional(),
    MODAL_SECRET: z.string().optional(),
    MODAL_WIKI_PROXY_URL: z.string().optional(),
    STRIPE_SECRET_KEY: stripeSecretKeySchema.optional(),
    STRIPE_WEBHOOK_SECRET: z.string().startsWith('whsec_').min(1).optional(),
    STRIPE_FLEX_CAPACITY_PRODUCT_ID: z.string().startsWith('prod_').optional(),
    DATABASE_URL_REPLICA: z.string().url().optional(),
    DATABASE_URL_REPLICA_USA: z.string().url().optional(),
    DATABASE_URL_REPLICA_SINGAPORE: z.string().url().optional(),
    CUSTOMER_IO_SITE_ID: z.string().optional(),
    CUSTOMER_IO_API_KEY: z.string().optional(),
    CUSTOMER_IO_APP_API_KEY: z.string().optional(),
    DEV_AI_FEATURES_ALLOWED_USERS: z.string().optional(),
    DEV_WEBHOOK_ALLOWED_ORGS: z.string().optional(),
    DEV_WEBHOOK_ALLOWED_REPOS: z.string().optional(),
    LOCAL_FORCE_ACTIVE_SUBSCRIPTION: z.enum(['true', 'false']).optional(),
    // Set 'true' to let a local server write the shared PR description in dev (see `cedesSharedPrDescription`).
    DEV_UPDATES_PR_DESCRIPTION: z.enum(['true', 'false']).optional(),
    USE_LOCAL_MODAL_LSPROXY: z.string().optional(),
    NO_AI_DEV_PROMPT_CACHE: z.string().optional(),
    FORCE_NEW_INSTALL: z.string().optional(),
    ATLAS_SECRET_KEY: z.string().optional(),
    LANGFUSE_SECRET_KEY: z.string().startsWith('sk-lf-').optional(),
    LANGFUSE_PUBLIC_KEY: z.string().startsWith('pk-lf-').optional(),
    LANGFUSE_BASE_URL: z.string().url().optional(),
    LANGFUSE_TRACE_EXPORT_S3_BUCKET: z.string().min(1).optional(),
    LANGFUSE_TRACE_EXPORT_S3_PREFIX: z.string().optional(),
    LANGFUSE_TRACING_ENABLED: z.enum(['true', 'false']).optional(),
    LANGFUSE_ARCHIVE_ENABLED: z.enum(['true', 'false']).optional(),
    AWS_ACCESS_KEY_ID: z.string().min(1).optional(),
    AWS_SECRET_ACCESS_KEY: z.string().min(1).optional(),
    AWS_REGION: z.string().min(1).optional(),
    EVALS_API_KEY: z.string().optional(),
    EXA_API_KEY: z.string().optional(),
    EXA_API_KEY_SECONDARY: z.string().optional(),
    FIRECRAWL_API_KEY: z.string().optional(),
    E2E_TEST_MODE: z.string().optional(),
    // Shared secret signing the short-lived grants that authorise the PR e2e contract
    // endpoints (/api/internal/e2e/*). Set on the Vercel Preview environment only.
    E2E_PROTO_SECRET: z.string().optional(),
    INCREMENTAL_LEARNINGS_ENABLED: z.string().optional(),
    // Pauses NEW pre-Cubic baseline admission (dashboard coordinate sends and
    // the coordinator). Workers keep draining so PENDING claims never orphan.
    REVIEW_LEAD_TIME_BASELINE_PAUSED: z.string().optional(),
    META_PIXEL_ID: z.string().optional(),
    META_CAPI_ACCESS_TOKEN: z.string().optional(),
    // Vercel Integration
    VERCEL_INTEGRATION_CLIENT_ID: z.string().startsWith('oac_').optional(), // OAuth client ID, also used for JWT audience verification
    VERCEL_INTEGRATION_SECRET: z.string().optional(), // Client secret (also used for webhook signature verification)
    VERCEL_WEBHOOK_PROXY_URL: z.string().optional(),
    // Vercel Sandbox authentication (see Vercel Sandbox docs):
    // - Local dev: `vercel link` + `vercel env pull` writes explicit SDK credentials to .env.vercel.local.
    VERCEL_SANDBOX_TOKEN: z.string().optional(),
    VERCEL_SANDBOX_TEAM_ID: z.string().optional(),
    // The Vercel project sandbox sessions run in (and whose VCR holds the
    // review image) — deliberately NOT always the linked app project.
    VERCEL_SANDBOX_PROJECT_ID: z.string().optional(),
    // Legacy names accepted during the VERCEL_SANDBOX_* credential rename.
    VERCEL_TEAM_ID: z.string().optional(),
    VERCEL_PROJECT_ID: z.string().optional(),
    VERCEL_SANDBOX_LSPROXY_IMAGE: z.string().optional(),
    CODING_AGENT_SANDBOX_IMAGE: z.string().optional(),
    CODING_AGENT_OPENCODE_SANDBOX_IMAGE: z.string().optional(),
    CODING_AGENT_GITHUB_PROXY_URL: z.string().url().optional(),
    CODING_AGENT_MCP_PROXY_URL: z.string().url().optional(),
    CODING_AGENT_SESSION_PROXY_URL: z.string().url().optional(),
    CODING_AGENT_GIT_SIGNING_KEY_ARN: z.string().optional(),
    CODING_AGENT_GIT_SIGNING_ROLE_ARN: z.string().optional(),
    CODING_AGENT_GIT_SIGNING_NAME: z.string().optional(),
    CODING_AGENT_GIT_SIGNING_EMAIL: z.string().optional(),
    // cubic Code: Ed25519 keypair for the sandbox front-door JWT (private is
    // PKCS8, public is SPKI; newlines may be stored as literal \n), the
    // installation allowlist (comma-separated ints), and the idle timeout.
    CODE_SANDBOX_JWT_PRIVATE_KEY: z.string().optional(),
    CODE_SANDBOX_JWT_PUBLIC_KEY: z.string().optional(),
    CODE_SESSIONS_INSTALLATION_IDS: z.string().optional(),
    CODE_SANDBOX_IDLE_TIMEOUT_MS: z.string().optional(),
    BL_WORKSPACE: z.string().optional(),
    BL_API_KEY: z.string().optional(),
    // Auto-fix loop alpha: installations (comma-separated ints) it is open to
    // beside cubic's own, which always have it.
    // Local development tunneling
    NGROK_AUTHTOKEN: z.string().optional(),
    // PostHog Private API (for querying person properties + sourcemap uploads)
    POSTHOG_PRIVATE_API_KEY: z.string().optional(),
    /**
     * Production read replica the complexity eval exporter checks account policy against before
     * it exports customer prompts. Eval-only; the exporter refuses to run without it.
     */
    COMPLEXITY_EVAL_ACCOUNT_POLICY_DATABASE_URL: z.string().url().optional(),
    POSTHOG_PROJECT_ID: z.string().optional(),
    // Observability alerting: Slack workflow-trigger webhook for the
    // #alerts-critical channel + read-only Sentry org token for the daily digest.
    ALERTS_CRITICAL_SLACK_WEBHOOK_URL: z.string().url().optional(),
    // Slack workflow-trigger webhook for #alerts-overview: weekly digests that
    // inform rather than page (AI marker registry health).
    ALERTS_OVERVIEW_SLACK_WEBHOOK_URL: z.string().url().optional(),
    // Slack workflow-trigger webhook for the growth channel: trial signup alerts
    // and the weekly trial expiration report. Unset skips them with a warning.
    GROWTH_CHANNEL_SLACK_WEBHOOK_URL: z.string().url().optional(),
    // Slack workflow-trigger webhook for the private dead-code channel: what the
    // post-merge check finds on internal repos. Unset skips posting with a warning.
    DEAD_CODE_CHANNEL_SLACK_WEBHOOK_URL: z.string().url().optional(),
    // Slack app incoming webhook for #social-radar (Block Kit alerts): public conversations
    // worth a reply, each with a draft. Unset keeps the daily social radar run from starting.
    SOCIAL_RADAR_CHANNEL_SLACK_WEBHOOK_URL: z.string().url().optional(),
    // Apify API token for the social radar's Reddit, X and LinkedIn scrapes.
    // Unset searches Hacker News only.
    APIFY_TOKEN: z.string().optional(),
    SENTRY_DIGEST_API_TOKEN: z.string().optional(),
    ENABLE_PUBLIC_PR_PAGES: z.string().optional(),
    ENABLE_CACHE_MATERIALIZER: z.string().optional(),
    PR_V2_WEBHOOK_INSTALLATION_ROLLOUT_PERCENT: emptyStringToZero(
      z.coerce.number().int().min(0).max(100).optional(),
    ),
    // Auto-injected by Vercel server-side on every deploy. Distinct from
    // `NEXT_PUBLIC_VERCEL_ENV`, the copy client-side code reads, which Next
    // inlines from the build's environment.
    VERCEL_ENV: z.enum(['production', 'preview', 'development']).optional(),
    // Auto-injected by Vercel server-side: the region running this function.
    VERCEL_REGION: z.string().optional(),
  },
  client: {
    NEXT_PUBLIC_LANGFUSE_BASE_URL: z.string().url().optional(),
    NEXT_PUBLIC_SITE_URL: z.string().optional(),
    NEXT_PUBLIC_LANGFUSE_PROJECT_ID: z.string().optional(),
    NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: supabasePublishableKeySchema,
    NEXT_PUBLIC_VERCEL_ENV: z.enum(['production', 'preview', 'development']).optional(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
  },
  shared: {
    NODE_ENV: z.enum(['test', 'development', 'production']).optional(),
    WEBHOOK_PROXY_URL: z.string().optional(),
  },
  // You need to destructure all the keys manually
  runtimeEnv: {
    ARCJET_KEY: process.env.ARCJET_KEY,
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_URL_QUEUE: process.env.DATABASE_URL_QUEUE,
    DIRECT_URL: process.env.DIRECT_URL,
    LOGTAIL_SOURCE_TOKEN: process.env.LOGTAIL_SOURCE_TOKEN,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    DIGITALOCEAN_INFERENCE_API_KEY: process.env.DIGITALOCEAN_INFERENCE_API_KEY,
    DIGITALOCEAN_INFERENCE_BASE_URL: process.env.DIGITALOCEAN_INFERENCE_BASE_URL,
    OPENROUTER_API_KEY: process.env.OPENROUTER_API_KEY,
    DOUBLEWORD_API_KEY: process.env.DOUBLEWORD_API_KEY,
    TYPESAFE_API_KEY: process.env.TYPESAFE_API_KEY,
    DOUBLEWORD_BASE_URL: process.env.DOUBLEWORD_BASE_URL,
    GPT_CODEX_SUBSCRIPTION_INSTALLATION_IDS: process.env.GPT_CODEX_SUBSCRIPTION_INSTALLATION_IDS,
    AI_GATEWAY_API_KEY: process.env.AI_GATEWAY_API_KEY,
    VERCEL_AI_GATEWAY_API_KEY: process.env.VERCEL_AI_GATEWAY_API_KEY,
    VERCEL_OIDC_TOKEN: process.env.VERCEL_OIDC_TOKEN,
    BLOB_STORE_ID: process.env.BLOB_STORE_ID,
    BLOB_READ_WRITE_TOKEN: process.env.BLOB_READ_WRITE_TOKEN,
    DATADOG_API_KEY: process.env.DATADOG_API_KEY,
    DATADOG_APPLICATION_KEY: process.env.DATADOG_APPLICATION_KEY,
    DATADOG_SITE: process.env.DATADOG_SITE,
    DATADOG_APPLICATION_KEY_ID: process.env.DATADOG_APPLICATION_KEY_ID,
    GITHUB_APP_ID: process.env.GITHUB_APP_ID,
    GITHUB_APP_PRIVATE_KEY_BASE64: process.env.GITHUB_APP_PRIVATE_KEY_BASE64,
    GITHUB_APP_WEBHOOK_SECRET: process.env.GITHUB_APP_WEBHOOK_SECRET,
    GITHUB_PUBLIC_ACCESS_TOKEN: process.env.GITHUB_PUBLIC_ACCESS_TOKEN,
    GITHUB_TOKEN: process.env.GITHUB_TOKEN,
    CUBIC_REVIEW_RUNTIME: process.env.CUBIC_REVIEW_RUNTIME,
    CUBIC_EVAL_CLI: process.env.CUBIC_EVAL_CLI,
    EVAL_RUN_ID: process.env.EVAL_RUN_ID,
    EVAL_DATASET_ID: process.env.EVAL_DATASET_ID,
    WEBHOOK_PROXY_URL: process.env.WEBHOOK_PROXY_URL,
    NANGO_WEBHOOK_PROXY_URL: process.env.NANGO_WEBHOOK_PROXY_URL,
    SUPABASE_AUTH_GITHUB_CLIENT_ID: process.env.SUPABASE_AUTH_GITHUB_CLIENT_ID,
    SUPABASE_AUTH_GITHUB_SECRET: process.env.SUPABASE_AUTH_GITHUB_SECRET,
    AUTO_APPROVAL_GITHUB_OAUTH_CLIENT_ID: process.env.AUTO_APPROVAL_GITHUB_OAUTH_CLIENT_ID,
    AUTO_APPROVAL_GITHUB_OAUTH_CLIENT_SECRET: process.env.AUTO_APPROVAL_GITHUB_OAUTH_CLIENT_SECRET,
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
    SUPABASE_SECRET_KEY: process.env.SUPABASE_SECRET_KEY,
    NODE_ENV: process.env.NODE_ENV,
    SENTRY_SUPPRESS_TURBOPACK_WARNING: process.env.SENTRY_SUPPRESS_TURBOPACK_WARNING,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    TENOR_API_KEY: process.env.TENOR_API_KEY,
    FRAMER_API_KEY: process.env.FRAMER_API_KEY,
    FRAMER_PROJECT_URL: process.env.FRAMER_PROJECT_URL,
    INDEXNOW_KEY: process.env.INDEXNOW_KEY,
    KV_URL: process.env.KV_URL,
    KV_REST_API_READ_ONLY_TOKEN: process.env.KV_REST_API_READ_ONLY_TOKEN,
    KV_REST_API_TOKEN: process.env.KV_REST_API_TOKEN,
    KV_REST_API_URL: process.env.KV_REST_API_URL,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    CRON_SECRET: process.env.CRON_SECRET,
    NANGO_SECRET_KEY: process.env.NANGO_SECRET_KEY,
    NANGO_WEBHOOK_SIGNING_KEY: process.env.NANGO_WEBHOOK_SIGNING_KEY,
    SLACK_CLIENT_ID: process.env.SLACK_CLIENT_ID,
    SLACK_CLIENT_SECRET: process.env.SLACK_CLIENT_SECRET,
    SLACK_ENCRYPTION_KEY: process.env.SLACK_ENCRYPTION_KEY,
    SLACK_SIGNING_SECRET: process.env.SLACK_SIGNING_SECRET,
    LINEAR_CLIENT_ID: process.env.LINEAR_CLIENT_ID,
    LINEAR_CLIENT_SECRET: process.env.LINEAR_CLIENT_SECRET,
    AZURE_RESOURCE_NAME: process.env.AZURE_RESOURCE_NAME,
    AZURE_API_KEY: process.env.AZURE_API_KEY,
    AZURE_SECONDARY_RESOURCE_NAME: process.env.AZURE_SECONDARY_RESOURCE_NAME,
    AZURE_SECONDARY_API_KEY: process.env.AZURE_SECONDARY_API_KEY,
    AZURE_TERTIARY_RESOURCE_NAME: process.env.AZURE_TERTIARY_RESOURCE_NAME,
    AZURE_TERTIARY_API_KEY: process.env.AZURE_TERTIARY_API_KEY,
    AZURE_QUATERNARY_RESOURCE_NAME: process.env.AZURE_QUATERNARY_RESOURCE_NAME,
    AZURE_QUATERNARY_API_KEY: process.env.AZURE_QUATERNARY_API_KEY,
    DEEPSEEK_API_KEY: process.env.DEEPSEEK_API_KEY,
    GOOGLE_GENERATIVE_AI_API_KEY: process.env.GOOGLE_GENERATIVE_AI_API_KEY,
    GOOGLE_VERTEX_PROJECT: process.env.GOOGLE_VERTEX_PROJECT,
    GOOGLE_VERTEX_SERVICE_ACCOUNT: process.env.GOOGLE_VERTEX_SERVICE_ACCOUNT,
    GOOGLE_VERTEX_API_KEY: process.env.GOOGLE_VERTEX_API_KEY,
    MODAL_KEY: process.env.MODAL_KEY,
    MODAL_SECRET: process.env.MODAL_SECRET,
    MODAL_WIKI_PROXY_URL: process.env.MODAL_WIKI_PROXY_URL,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_FLEX_CAPACITY_PRODUCT_ID: process.env.STRIPE_FLEX_CAPACITY_PRODUCT_ID,
    DATABASE_URL_REPLICA: process.env.DATABASE_URL_REPLICA,
    DATABASE_URL_REPLICA_USA: process.env.DATABASE_URL_REPLICA_USA,
    DATABASE_URL_REPLICA_SINGAPORE: process.env.DATABASE_URL_REPLICA_SINGAPORE,
    CUSTOMER_IO_SITE_ID: process.env.CUSTOMER_IO_SITE_ID,
    CUSTOMER_IO_API_KEY: process.env.CUSTOMER_IO_API_KEY,
    CUSTOMER_IO_APP_API_KEY: process.env.CUSTOMER_IO_APP_API_KEY,
    DEV_AI_FEATURES_ALLOWED_USERS: process.env.DEV_AI_FEATURES_ALLOWED_USERS,
    DEV_WEBHOOK_ALLOWED_ORGS: process.env.DEV_WEBHOOK_ALLOWED_ORGS,
    DEV_WEBHOOK_ALLOWED_REPOS: process.env.DEV_WEBHOOK_ALLOWED_REPOS,
    LOCAL_FORCE_ACTIVE_SUBSCRIPTION: process.env.LOCAL_FORCE_ACTIVE_SUBSCRIPTION,
    DEV_UPDATES_PR_DESCRIPTION: process.env.DEV_UPDATES_PR_DESCRIPTION,
    USE_LOCAL_MODAL_LSPROXY: process.env.USE_LOCAL_MODAL_LSPROXY,
    NO_AI_DEV_PROMPT_CACHE: process.env.NO_AI_DEV_PROMPT_CACHE,
    FORCE_NEW_INSTALL: process.env.FORCE_NEW_INSTALL,
    ATLAS_SECRET_KEY: process.env.ATLAS_SECRET_KEY,
    LANGFUSE_SECRET_KEY: process.env.LANGFUSE_SECRET_KEY,
    LANGFUSE_PUBLIC_KEY: process.env.LANGFUSE_PUBLIC_KEY,
    // Preserve compatibility with older self-hosted deployments during the env var rename.
    LANGFUSE_BASE_URL: process.env.LANGFUSE_BASE_URL ?? process.env.LANGFUSE_BASEURL,
    LANGFUSE_TRACE_EXPORT_S3_BUCKET: process.env.LANGFUSE_TRACE_EXPORT_S3_BUCKET,
    LANGFUSE_TRACE_EXPORT_S3_PREFIX: process.env.LANGFUSE_TRACE_EXPORT_S3_PREFIX,
    LANGFUSE_TRACING_ENABLED: process.env.LANGFUSE_TRACING_ENABLED,
    LANGFUSE_ARCHIVE_ENABLED: process.env.LANGFUSE_ARCHIVE_ENABLED,
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    AWS_REGION: process.env.AWS_REGION,
    EVALS_API_KEY: process.env.EVALS_API_KEY,
    EXA_API_KEY: process.env.EXA_API_KEY,
    EXA_API_KEY_SECONDARY: process.env.EXA_API_KEY_SECONDARY,
    FIRECRAWL_API_KEY: process.env.FIRECRAWL_API_KEY,
    E2E_TEST_MODE: process.env.E2E_TEST_MODE,
    E2E_PROTO_SECRET: process.env.E2E_PROTO_SECRET,
    INCREMENTAL_LEARNINGS_ENABLED: process.env.INCREMENTAL_LEARNINGS_ENABLED,
    REVIEW_LEAD_TIME_BASELINE_PAUSED: process.env.REVIEW_LEAD_TIME_BASELINE_PAUSED,
    NEXT_PUBLIC_LANGFUSE_BASE_URL:
      process.env.NEXT_PUBLIC_LANGFUSE_BASE_URL ?? process.env.NEXT_PUBLIC_LANGFUSE_BASEURL,
    NEXT_PUBLIC_LANGFUSE_PROJECT_ID: process.env.NEXT_PUBLIC_LANGFUSE_PROJECT_ID,
    META_PIXEL_ID: process.env.META_PIXEL_ID,
    META_CAPI_ACCESS_TOKEN: process.env.META_CAPI_ACCESS_TOKEN,
    // Vercel Integration
    VERCEL_INTEGRATION_CLIENT_ID: process.env.VERCEL_INTEGRATION_CLIENT_ID,
    VERCEL_INTEGRATION_SECRET: process.env.VERCEL_INTEGRATION_SECRET,
    VERCEL_WEBHOOK_PROXY_URL: process.env.VERCEL_WEBHOOK_PROXY_URL,
    VERCEL_SANDBOX_TOKEN: process.env.VERCEL_SANDBOX_TOKEN,
    VERCEL_SANDBOX_TEAM_ID: process.env.VERCEL_SANDBOX_TEAM_ID,
    VERCEL_SANDBOX_PROJECT_ID: process.env.VERCEL_SANDBOX_PROJECT_ID,
    VERCEL_TEAM_ID: process.env.VERCEL_TEAM_ID,
    VERCEL_PROJECT_ID: process.env.VERCEL_PROJECT_ID,
    VERCEL_SANDBOX_LSPROXY_IMAGE: process.env.VERCEL_SANDBOX_LSPROXY_IMAGE,
    CODING_AGENT_SANDBOX_IMAGE: process.env.CODING_AGENT_SANDBOX_IMAGE,
    CODING_AGENT_OPENCODE_SANDBOX_IMAGE: process.env.CODING_AGENT_OPENCODE_SANDBOX_IMAGE,
    CODING_AGENT_GITHUB_PROXY_URL: process.env.CODING_AGENT_GITHUB_PROXY_URL,
    CODING_AGENT_MCP_PROXY_URL: process.env.CODING_AGENT_MCP_PROXY_URL,
    CODING_AGENT_SESSION_PROXY_URL: process.env.CODING_AGENT_SESSION_PROXY_URL,
    CODING_AGENT_GIT_SIGNING_KEY_ARN: process.env.CODING_AGENT_GIT_SIGNING_KEY_ARN,
    CODING_AGENT_GIT_SIGNING_ROLE_ARN: process.env.CODING_AGENT_GIT_SIGNING_ROLE_ARN,
    CODING_AGENT_GIT_SIGNING_NAME: process.env.CODING_AGENT_GIT_SIGNING_NAME,
    CODING_AGENT_GIT_SIGNING_EMAIL: process.env.CODING_AGENT_GIT_SIGNING_EMAIL,
    CODE_SANDBOX_JWT_PRIVATE_KEY: process.env.CODE_SANDBOX_JWT_PRIVATE_KEY,
    CODE_SANDBOX_JWT_PUBLIC_KEY: process.env.CODE_SANDBOX_JWT_PUBLIC_KEY,
    CODE_SESSIONS_INSTALLATION_IDS: process.env.CODE_SESSIONS_INSTALLATION_IDS,
    CODE_SANDBOX_IDLE_TIMEOUT_MS: process.env.CODE_SANDBOX_IDLE_TIMEOUT_MS,
    BL_WORKSPACE: process.env.BL_WORKSPACE,
    BL_API_KEY: process.env.BL_API_KEY,
    // Local development tunneling
    NGROK_AUTHTOKEN: process.env.NGROK_AUTHTOKEN,
    POSTHOG_PRIVATE_API_KEY: process.env.POSTHOG_PRIVATE_API_KEY,
    COMPLEXITY_EVAL_ACCOUNT_POLICY_DATABASE_URL:
      process.env.COMPLEXITY_EVAL_ACCOUNT_POLICY_DATABASE_URL,
    POSTHOG_PROJECT_ID: process.env.POSTHOG_PROJECT_ID,
    ALERTS_CRITICAL_SLACK_WEBHOOK_URL: process.env.ALERTS_CRITICAL_SLACK_WEBHOOK_URL,
    ALERTS_OVERVIEW_SLACK_WEBHOOK_URL: process.env.ALERTS_OVERVIEW_SLACK_WEBHOOK_URL,
    GROWTH_CHANNEL_SLACK_WEBHOOK_URL: process.env.GROWTH_CHANNEL_SLACK_WEBHOOK_URL,
    DEAD_CODE_CHANNEL_SLACK_WEBHOOK_URL: process.env.DEAD_CODE_CHANNEL_SLACK_WEBHOOK_URL,
    SOCIAL_RADAR_CHANNEL_SLACK_WEBHOOK_URL: process.env.SOCIAL_RADAR_CHANNEL_SLACK_WEBHOOK_URL,
    APIFY_TOKEN: process.env.APIFY_TOKEN,
    SENTRY_DIGEST_API_TOKEN: process.env.SENTRY_DIGEST_API_TOKEN,
    ENABLE_PUBLIC_PR_PAGES: process.env.ENABLE_PUBLIC_PR_PAGES,
    ENABLE_CACHE_MATERIALIZER: process.env.ENABLE_CACHE_MATERIALIZER,
    PR_V2_WEBHOOK_INSTALLATION_ROLLOUT_PERCENT:
      process.env.PR_V2_WEBHOOK_INSTALLATION_ROLLOUT_PERCENT,
    VERCEL_ENV: process.env.VERCEL_ENV,
    VERCEL_REGION: process.env.VERCEL_REGION,
  },
});

export const isPreview = () => {
  return Env.NEXT_PUBLIC_VERCEL_ENV === 'preview';
};

// Helper function to determine if we're in development/local environment
export const isDevelopment = () => {
  return (
    Env.NODE_ENV === 'development' ||
    Env.NEXT_PUBLIC_VERCEL_ENV === 'development' ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost')
  );
};

// Whether a local server should cede the shared PR description to production; false outside dev, and true in dev unless DEV_UPDATES_PR_DESCRIPTION=true.
export const cedesSharedPrDescription = () => {
  return isDevelopment() && Env.DEV_UPDATES_PR_DESCRIPTION !== 'true';
};

/**
 * Utility function to get the appropriate app name based on the current environment
 * @returns The appropriate APP_NAME constant for the current environment
 */
export const getAppName = () => {
  if (isDevelopment()) {
    return APP_NAME.DEV;
  } else if (Env.NEXT_PUBLIC_VERCEL_ENV === 'preview') {
    return APP_NAME.STAGING;
  }
  return APP_NAME.PROD;
};

/** Telemetry label for the runtime environment a request was served from. */
export type DeploymentEnvironment = 'production' | 'preview' | 'development';

/**
 * Returns the runtime environment for telemetry tagging on `$ai_generation`
 * events (PostHog) and Langfuse traces.
 *
 * - `production` on Vercel production deploys.
 * - `preview` on Vercel preview deploys.
 * - `development` on local dev (`next dev`), tests (`vitest`), and any other
 *   non-prod runtime with `NODE_ENV !== 'production'`.
 *
 * Bias when `VERCEL_ENV` is unset: defaults to `production` if `NODE_ENV` is
 * `production`. The intent is asymmetric — we'd rather over-attribute to prod
 * than silently misattribute a Vercel production deploy with a missing
 * `VERCEL_ENV` as `development`. The reverse misattribution (a local
 * `next build && next start` shows up as `production`) is rare, low-volume,
 * and far less harmful than corrupting the prod cost slice.
 *
 * Server-only. Reads bare `VERCEL_ENV` (which Vercel auto-injects on every
 * deploy). Client-side code would read `NEXT_PUBLIC_VERCEL_ENV` instead, which
 * Next inlines from the build's environment.
 */
export const getVercelEnvironment = (): DeploymentEnvironment => {
  if (Env.VERCEL_ENV) {
    return Env.VERCEL_ENV;
  }
  return Env.NODE_ENV === 'production' ? 'production' : 'development';
};

// `pnpm dev` sets NEXT_PUBLIC_SITE_URL to another local port when a dev server
// in another worktree already holds 3000 (scripts/dev-ports.js).
const LOCAL_DEVELOPMENT_ORIGIN = /^http:\/\/localhost:\d+/;

export const baseUrl = (): string => {
  // In development, use localhost
  if (isDevelopment()) {
    return (
      Env.NEXT_PUBLIC_SITE_URL?.match(LOCAL_DEVELOPMENT_ORIGIN)?.[0] ?? 'http://localhost:3000'
    );
  }

  // In preview deployments (Vercel), stay on the deployment being served. The
  // browser only sees NEXT_PUBLIC_* vars, so client code uses the current origin.
  if (Env.NEXT_PUBLIC_VERCEL_ENV === 'preview') {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    const previewHost = process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL || process.env.VERCEL_URL;
    return previewHost ? `https://${previewHost}` : Env.NEXT_PUBLIC_SITE_URL || 'https://cubic.dev';
  }

  // In production, use the configured site URL or fallback to cubic.dev
  return Env.NEXT_PUBLIC_SITE_URL || 'https://cubic.dev';
};
